import { Check, X } from 'lucide-react';
import type { StrategyQuestion } from '../api/strategyApi';
import { Badge } from '@/shared/ui';
import styles from './PracticeProfileStep.module.css';
import localStyles from './QuestionInput.module.css';

export function QuestionInput({
  question: q,
  value,
  onChange,
}: {
  question: StrategyQuestion;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  switch (q.type) {
    case 'select':
      return (
        <select
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value || undefined)}
          className={localStyles.formInput}
        >
          <option value="">Select...</option>
          {q.options?.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
          {/* An answer captured on a call that isn't one of the options. */}
          {typeof value === 'string' && value && !q.options?.includes(value) && (
            <option value={value}>{value}</option>
          )}
        </select>
      );

    case 'textarea':
      return (
        <textarea
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className={`${localStyles.formInput} ${localStyles.formTextarea}`}
        />
      );

    case 'text':
      return (
        <input
          type="text"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={localStyles.formInput}
        />
      );

    case 'checkboxes':
    case 'multiselect':
      return <CheckboxesInput q={q} value={value} onChange={onChange} />;

    case 'cards':
      return <CardsInput q={q} value={value} onChange={onChange} />;

    case 'multitext':
      return <MultitextInput q={q} value={value} onChange={onChange} />;

    case 'ranked':
      return <RankedInput q={q} value={value} onChange={onChange} />;

    default:
      return (
        <input
          type="text"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={localStyles.formInput}
        />
      );
  }
}

// ─── Answers that aren't one of the options ───────────────────────

/** Selected values that are neither an option nor an "Other" entry. */
function offListValues(selected: string[], options: string[] | null | undefined): string[] {
  return selected.filter(
    (v) => !(options ?? []).includes(v) && !v.startsWith('Other: ') && v !== 'Other',
  );
}

/** Off-list answers (typically captured on a call) shown selected, click to remove. */
function OffListChips({
  extras,
  selected,
  onChange,
}: {
  extras: string[];
  selected: string[];
  onChange: (v: unknown) => void;
}) {
  return (
    <>
      {extras.map((v) => (
        <div
          key={`extra-${v}`}
          onClick={() => onChange(selected.filter((s) => s !== v))}
          className={`${styles.optionChip} ${styles.optionChipSel}`}
          title="From your intake call — click to remove"
        >
          {v} <X size={11} aria-hidden />
        </div>
      ))}
    </>
  );
}

// ─── Other toggle helpers (shared by checkboxes + multitext) ──────

function useOtherEntries(selected: string[], options: string[] | null | undefined) {
  const hasOtherOption = options?.includes('Other') ?? false;
  const predefinedOpts = hasOtherOption ? options!.filter((o) => o !== 'Other') : (options ?? []);
  const otherEntries = selected.filter((v) => v.startsWith('Other: ') || v === 'Other');
  const otherTexts = otherEntries.map((v) => (v.startsWith('Other: ') ? v.slice(7) : ''));
  const hasOtherSelected = otherEntries.length > 0;
  return { hasOtherOption, predefinedOpts, otherTexts, hasOtherSelected };
}

function OtherEntriesEditor({
  selected,
  otherTexts,
  predefined,
  onChange,
  placeholder = 'Please specify…',
  otherLimit,
}: {
  selected: string[];
  otherTexts: string[];
  predefined: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  otherLimit: number;
}) {
  const updateOtherText = (i: number, text: string) => {
    const nonOther = predefined;
    const newOthers = [...otherTexts];
    newOthers[i] = text;
    onChange([...nonOther, ...newOthers.map((t) => (t ? `Other: ${t}` : 'Other'))]);
  };

  const removeOtherEntry = (i: number) => {
    const newOthers = otherTexts.filter((_, idx) => idx !== i);
    if (newOthers.length === 0) {
      onChange(predefined);
    } else {
      onChange([...predefined, ...newOthers.map((t) => (t ? `Other: ${t}` : 'Other'))]);
    }
  };

  const addOtherEntry = () => {
    if (otherTexts.length < otherLimit) onChange([...selected, 'Other']);
  };

  return (
    <div className={styles.editorCol}>
      {otherTexts.map((text, i) => (
        <div key={i} className={styles.otherEntryRow}>
          <input
            type="text"
            value={text}
            onChange={(e) => updateOtherText(i, e.target.value)}
            placeholder={placeholder}
            className={`${localStyles.formInput} ${localStyles.flex1}`}
          />
          <span onClick={() => removeOtherEntry(i)} className={styles.removeX}>
            <X size={12} aria-hidden />
          </span>
        </div>
      ))}
      {otherTexts.length < otherLimit && (
        <span onClick={addOtherEntry} className={styles.addAnother}>
          + Add another
        </span>
      )}
    </div>
  );
}

// ─── Checkboxes / Multiselect ─────────────────────────────────────

function CheckboxesInput({
  q,
  value,
  onChange,
}: {
  q: StrategyQuestion;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const selected = Array.isArray(value) ? (value as string[]) : [];
  const { hasOtherOption, predefinedOpts, otherTexts, hasOtherSelected } = useOtherEntries(
    selected,
    q.options,
  );

  const extras = offListValues(selected, q.options);

  const toggleOther = () => {
    if (hasOtherSelected) {
      onChange(selected.filter((v) => !v.startsWith('Other: ') && v !== 'Other'));
    } else {
      onChange([...selected, 'Other']);
    }
  };

  return (
    <div className={styles.editorCol}>
      <div className={styles.optionGrid}>
        <OffListChips extras={extras} selected={selected} onChange={onChange} />
        {predefinedOpts.map((o) => {
          const sel = selected.includes(o);
          return (
            <div
              key={o}
              onClick={() => onChange(sel ? selected.filter((s) => s !== o) : [...selected, o])}
              className={`${styles.optionChip} ${sel ? styles.optionChipSel : styles.optionChipUnsel}`}
            >
              {o}
            </div>
          );
        })}
        {hasOtherOption && (
          <div
            onClick={toggleOther}
            className={`${styles.optionChip} ${hasOtherSelected ? styles.optionChipSel : styles.optionChipUnsel}`}
          >
            Other
          </div>
        )}
      </div>
      {hasOtherSelected && (
        <OtherEntriesEditor
          selected={selected}
          otherTexts={otherTexts}
          predefined={selected.filter((v) => !v.startsWith('Other: ') && v !== 'Other')}
          onChange={(v) => onChange(v)}
          otherLimit={q.otherLimit}
        />
      )}
    </div>
  );
}

// ─── Cards (pill-style multi-select) ──────────────────────────────

function CardsInput({
  q,
  value,
  onChange,
}: {
  q: StrategyQuestion;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const selected = Array.isArray(value) ? (value as string[]) : [];
  return (
    <div className={styles.optionPillWrap}>
      <OffListChips
        extras={offListValues(selected, q.options)}
        selected={selected}
        onChange={onChange}
      />
      {q.options?.map((o) => {
        const sel = selected.includes(o);
        return (
          <div
            key={o}
            onClick={() => onChange(sel ? selected.filter((s) => s !== o) : [...selected, o])}
            className={`${styles.optionPill} ${sel ? styles.optionChipSel : styles.optionChipUnsel}`}
          >
            {o}
          </div>
        );
      })}
    </div>
  );
}

// ─── Multitext (checkbox list with "Other") ───────────────────────

function MultitextInput({
  q,
  value,
  onChange,
}: {
  q: StrategyQuestion;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const selected = Array.isArray(value) ? (value as string[]) : [];
  const predefined = selected.filter((s) => q.options?.includes(s));
  const otherEntries = selected.filter((s) => !q.options?.includes(s));
  const otherTexts = otherEntries.map((v) =>
    v.startsWith('Other: ') ? v.slice(7) : v === 'Other' ? '' : v,
  );
  const hasOtherSelected = otherEntries.length > 0;
  const totalCount = predefined.length + otherEntries.length;

  const toggleOther = () => {
    if (hasOtherSelected) {
      onChange(predefined);
    } else {
      onChange([...selected, 'Other']);
    }
  };

  return (
    <div className={styles.editorColSm}>
      {q.options?.map((o) => {
        const sel = predefined.includes(o);
        return (
          <div
            key={o}
            onClick={() => {
              if (sel) onChange(selected.filter((s) => s !== o));
              else if (totalCount < 5) onChange([...selected, o]);
            }}
            className={`${styles.checkRow} ${sel ? styles.checkRowSel : styles.checkRowUnsel}${!sel && totalCount >= 5 ? ` ${styles.checkRowDisabled}` : ''}`}
          >
            <span
              className={`${styles.checkbox} ${sel ? styles.checkboxSel : styles.checkboxUnsel}`}
            >
              {sel && <Check size={11} aria-hidden />}
            </span>
            <span className={sel ? styles.checkRowTextSel : styles.checkRowTextUnsel}>{o}</span>
          </div>
        );
      })}
      <div
        onClick={toggleOther}
        className={`${styles.optionChip} ${hasOtherSelected ? styles.optionChipSel : styles.optionChipUnsel}`}
      >
        Other
      </div>
      {hasOtherSelected && (
        <OtherEntriesEditor
          selected={selected}
          otherTexts={otherTexts}
          predefined={predefined}
          onChange={(v) => onChange(v)}
          placeholder="Enter your question…"
          otherLimit={q.otherLimit}
        />
      )}
    </div>
  );
}

// ─── Ranked (click to add rank, ordered) ──────────────────────────

function RankedInput({
  q,
  value,
  onChange,
}: {
  q: StrategyQuestion;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const allOpts: string[] = q.options ?? [];
  const hasOtherOption = allOpts.includes('Other');
  const predefinedOpts = hasOtherOption ? allOpts.filter((o) => o !== 'Other') : allOpts;
  const rawSelected: string[] = Array.isArray(value) ? (value as string[]) : [];
  // Values that aren't options (e.g. captured on a call) stay, shown as custom
  // entries — filtering them out lost them on the next change.
  const selected = rawSelected;
  const isOtherItem = (s: string) => !predefinedOpts.includes(s);
  const otherCount = selected.filter(isOtherItem).length;
  const otherDisplay = (s: string) =>
    s.startsWith('Other: ') ? s.slice(7) : s === 'Other' ? '' : s;

  const getRank = (item: string) => {
    const idx = selected.indexOf(item);
    return idx >= 0 ? idx + 1 : 0;
  };

  const togglePredefined = (item: string) => {
    if (selected.includes(item)) {
      onChange(selected.filter((s) => s !== item));
    } else {
      onChange([...selected, item]);
    }
  };

  const removeAtIndex = (idx: number) => {
    onChange(selected.filter((_, i) => i !== idx));
  };

  const updateOtherAtIndex = (idx: number, text: string) => {
    const next = [...selected];
    next[idx] = text ? `Other: ${text}` : 'Other';
    onChange(next);
  };

  const addOtherEntry = () => {
    if (otherCount < q.otherLimit) onChange([...selected, 'Other']);
  };

  const otherEntries = selected
    .map((item, idx) => ({ item, idx }))
    .filter(({ item }) => isOtherItem(item));

  return (
    <div className={styles.editorColSm}>
      {predefinedOpts.map((item) => {
        const rank = getRank(item);
        const sel = rank > 0;
        return (
          <div
            key={item}
            onClick={() => togglePredefined(item)}
            className={`${styles.checkRow} ${sel ? styles.checkRowSel : styles.checkRowUnsel}`}
          >
            {sel ? (
              <span className={styles.rankBadge}>#{rank}</span>
            ) : (
              <span className={styles.rankPlus}>+</span>
            )}
            <span className={sel ? styles.rankedItemSel : styles.rankedItemUnsel}>{item}</span>
            {sel && (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  togglePredefined(item);
                }}
                className={styles.removeX}
                title="Remove"
              >
                <X size={12} aria-hidden />
              </span>
            )}
          </div>
        );
      })}
      {otherEntries.map(({ item, idx }) => {
        const rank = idx + 1;
        return (
          <div key={`other-${idx}`} className={`${styles.checkRow} ${styles.rankedOtherRow}`}>
            <span className={styles.rankBadge}>#{rank}</span>
            <input
              type="text"
              value={otherDisplay(item)}
              onChange={(e) => updateOtherAtIndex(idx, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="Please specify…"
              className={styles.rankedInlineInput}
            />
            <Badge variant="primary">Custom</Badge>
            <span onClick={() => removeAtIndex(idx)} className={styles.removeX} title="Remove">
              <X size={12} aria-hidden />
            </span>
          </div>
        );
      })}
      {hasOtherOption && otherCount < q.otherLimit && (
        <div onClick={addOtherEntry} className={`${styles.checkRow} ${styles.rankedAddOtherRow}`}>
          <span className={styles.rankPlus}>+</span>
          <span className={styles.rankedItemUnsel}>Other</span>
        </div>
      )}
    </div>
  );
}
